import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { HeartIcon, ExternalLinkIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { featuredNonprofits as nonprofits, FeaturedNonprofit as Nonprofit } from '../data/nonprofits-featured';
function NonprofitCard({
  org
}: {
  org: Nonprofit;
}) {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  return <div className={`flex-shrink-0 flex items-center gap-3 px-5 py-3 rounded-2xl border border-gray-200 bg-white shadow-sm mx-3 group cursor-default select-none`} style={{
    minWidth: '240px'
  }} data-id="element-3257">
      <div className={`w-10 h-10 rounded-xl ${org.bgColor} flex items-center justify-center flex-shrink-0 text-xl overflow-hidden`} data-id="element-3258">
        {org.logoUrl ? <img src={org.logoUrl} alt={org.name} className="w-8 h-8 object-contain" data-id="element-3259" /> : org.emoji}
      </div>
      <div className="flex flex-col min-w-0" data-id="element-3260">
        <span className={`font-bebas text-base tracking-wider leading-tight ${org.accentColor}`} data-id="element-3261">
          {org.name}
        </span>
        <span className="font-nunito text-xs text-gray-500 leading-tight truncate" data-id="element-3262">
          {isEs && org.taglineEs ? org.taglineEs : org.tagline}
        </span>
      </div>
      {org.url && <a href={org.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="ml-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" aria-label={`${isEs ? 'Visitar' : 'Visit'} ${org.name}`} data-id="element-3263">
          <ExternalLinkIcon className="w-3.5 h-3.5 text-gray-400 hover:text-haa-blue transition-colors" data-id="element-3264" />
        </a>}
    </div>;
}
export function NonprofitScroller() {
  const titleRef = useRef<HTMLDivElement>(null);
  const inView = useInView(titleRef, {
    once: true
  });
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  // Duplicate for seamless loop
  const doubled = [...nonprofits, ...nonprofits];
  return <section className="bg-haa-navy/5 py-10 overflow-hidden" aria-label={isEs ? 'Organizaciones destacadas' : 'Featured nonprofits'} data-id="element-3265">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6" data-id="element-3266">
        <motion.div ref={titleRef} initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="flex items-center gap-3" data-id="element-3267">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-haa-blue to-haa-midblue flex items-center justify-center shadow-sm flex-shrink-0" data-id="element-3268">
            <HeartIcon className="w-4 h-4 text-white" data-id="element-3269" />
          </div>
          <div data-id="element-3270">
            <span className="font-nunito text-haa-blue text-xs uppercase tracking-[0.3em] font-semibold block leading-none mb-0.5" data-id="element-3271">
              {isEs ? 'Campeones de Honduras' : 'Honduras Champions'}
            </span>
            <h2 className="font-bebas text-2xl text-haa-navy tracking-wider leading-none" data-id="element-3272">
              {isEs ? 'Organizaciones Destacadas Del Mes' : 'Featured Organizations Of The Month'}
            </h2>
          </div>
        </motion.div>
      </div>

      {/* Scroller track */}
      <div className="relative w-full" style={{
      maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
      WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)'
    }} data-id="element-3273">
        <div className="flex nonprofit-scroll" style={{
        width: 'max-content'
      }} onMouseEnter={e => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.animationPlayState = 'paused';
      }} onMouseLeave={e => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.animationPlayState = 'running';
      }} data-id="element-3274">
          {doubled.map((org, i) => <NonprofitCard key={`${org.name}-${i}`} org={org} data-id="element-3275" />)}
        </div>
      </div>
    </section>;
}